import createAxiosInstance from '../support/AxiosSupport';

/**
 * Client-side cache for static reference data served from `/api/refs/*`.
 *
 * Reference dictionaries (categories, colors, sizes, …) change rarely, yet every
 * component fetched them on each mount, hammering the API on navigation (CTHS-110).
 * This module-level cache is shared across all components and mounts, so a repeated
 * mount within the TTL is served from memory without a network request. Concurrent
 * requests for the same ref are also de-duped into a single in-flight call.
 *
 * The TTL mirrors the backend `Cache-Control: public, max-age=604800` (7 days);
 * `clearRefsCache()` allows explicit invalidation when fresher data is required.
 */
const REFS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface CacheEntry {
    data: unknown;
    expiresAt: number;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<unknown>>();

function cacheKeyFor(path: string, params?: Record<string, string>): string {
    return params ? `${path}?${new URLSearchParams(params).toString()}` : path;
}

/**
 * Fetch a reference dictionary by its `/refs/<path>` suffix (e.g. "categories"),
 * returning cached data while it is fresh. `params` (e.g. `{ownerId}`) is folded into
 * the cache key — needed for "brands", which is owner-scoped rather than a global
 * taxonomy like the rest of this dictionary (see `entity.Brand`).
 */
export async function fetchRef<T>(path: string, params?: Record<string, string>): Promise<T> {
    const cacheKey = cacheKeyFor(path, params);
    const cached = cache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
        return cached.data as T;
    }

    const existing = inflight.get(cacheKey);
    if (existing) {
        return existing as Promise<T>;
    }

    const promise = (async () => {
        const api = await createAxiosInstance();
        const res = params
            ? await api.get<T>(`/refs/${path}`, {params})
            : await api.get<T>(`/refs/${path}`);
        cache.set(cacheKey, {data: res.data, expiresAt: Date.now() + REFS_TTL_MS});
        return res.data;
    })();

    inflight.set(cacheKey, promise);
    try {
        return (await promise) as T;
    } finally {
        inflight.delete(cacheKey);
    }
}

/** Drop all cached reference data (e.g. after a known backend update). */
export function clearRefsCache(): void {
    cache.clear();
    inflight.clear();
}

/** Drop one cached entry (e.g. "brands" after creating a new one) without wiping the rest. */
export function invalidateRef(path: string, params?: Record<string, string>): void {
    const cacheKey = cacheKeyFor(path, params);
    cache.delete(cacheKey);
    inflight.delete(cacheKey);
}
